import { routes } from '@platform/core/lib/routes';
import { groupEntries, moduleNav, systemItem } from '@platform/navigation';
import { appRoutes } from './appRoutes';
import type { IGroupRowsInput } from './IGroupRowsInput';
import type { IMenuRow } from './IMenuRow';
import { moduleCount } from './moduleCount';

/** Системні пункти, що мають сторінку в застосунку: Головна, Центр пошуку, Налаштування, Адміністрування; теги — платформний модуль tag. */
const SYSTEM_PAGES: Record<string, () => string> = {
  home: () => routes.home(),
  search: () => routes.search(),
  tags: () => routes.module('tag'),
  tagManagement: () => routes.module('tag'),
  customization: () => appRoutes.customize(),
  administration: () => appRoutes.admin(),
};

/** Системний модуль, якому відповідає пункт меню (активний, коли відкрито його сторінки). */
const SYSTEM_OF: Record<string, string> = { customization: 'customize', administration: 'admin' };

/** Модуль «Верховна Рада» (депутатські мандати): у меню «Рада» поза парламентським простором — орієнтир без лічильника (прототип v4). */
const LANDMARK = 'mandate';

/**
 * Рядки випадного меню групи верхньої панелі (прототип v4): системні пункти до й після модулів, модулі групи в порядку простору (ярлик
 * «модуль/тип» — список типу запису іншого модуля), підгрупи з малими заголовками (крім тих, що в режимі мають власну пігулку). Модулі,
 * не встановлені в робочому просторі, пропускаються. «Верховна Рада» поза парламентським простором — орієнтир (ключ l:) без лічильника,
 * незалежно від того, чи встановлено модуль у цьому просторі (веде до парламенту).
 */
export function groupRows(input: IGroupRowsInput): IMenuRow[] {
  const { group, catalog, module, recordType, onHome, label, onSystem } = input;
  const rows: IMenuRow[] = [];
  let skipping = false;
  for (const entry of groupEntries(group, input.parliament ?? true)) {
    if (entry.kind === 'heading') {
      skipping = !!input.omitSubgroups?.includes(entry.key);
      if (!skipping) rows.push({ key: `h:${entry.key}`, kind: 'heading', label: label(entry.label) });
      continue;
    }
    if (skipping && entry.kind === 'module') continue;
    if (entry.kind === 'system') {
      const item = systemItem(entry.key);
      if (!item) continue;
      const page = SYSTEM_PAGES[entry.key];
      rows.push({
        key: `s:${entry.key}`, kind: 'item', label: label(item.label), icon: item.icon, tone: item.tone,
        to: page?.(), onSelect: page ? undefined : () => onSystem(entry.key),
        active: (entry.key === 'home' && onHome) || ((entry.key === 'tags' || entry.key === 'tagManagement') && module === 'tag')
          || (!!input.system && SYSTEM_OF[entry.key] === input.system),
      });
      continue;
    }
    if (entry.key === LANDMARK && !entry.recordType && input.parliament === false) {
      const any = catalog?.modules.find((m) => m.key === entry.key);
      if (any) rows.push({ key: `l:${entry.key}`, kind: 'item', label: label(any.name), icon: 'landmark', tone: moduleNav(entry.key).tone, ...(input.onLandmark ? { onSelect: input.onLandmark } : { to: routes.module(entry.key) }), active: module === entry.key });
      continue;
    }
    const summary = catalog?.modules.find((m) => m.key === entry.key && m.installed);
    if (!summary) continue;
    const rt = entry.recordType ? summary.recordTypes.find((r) => r.key === entry.recordType) : undefined;
    if (entry.recordType && !rt) continue;
    const nav = moduleNav(entry.key);
    if (entry.key === LANDMARK && !rt && input.parliament) {
      rows.push({ key: `m:${entry.key}`, kind: 'item', label: label(summary.name), icon: 'layout-grid', tone: nav.tone, count: moduleCount(summary), to: routes.module(entry.key), active: module === entry.key });
      continue;
    }
    rows.push({
      key: `m:${entry.key}${rt ? `/${rt.key}` : ''}`, kind: 'item', label: label(rt?.name ?? summary.name), icon: nav.icon, tone: nav.tone,
      count: moduleCount(summary, rt?.key), to: rt ? routes.list(entry.key, rt.key) : routes.module(entry.key),
      active: module === entry.key && (!rt || recordType === rt.key),
    });
  }
  return rows;
}
